import { useMemo } from 'react';
import { isNotNullish } from '@chainflip/utils/guard';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import classNames from 'classnames';
import { MacScrollbar } from 'mac-scrollbar';
import { AnimatePresence, motion } from 'motion/react';
import { type ChainflipToken } from '@/shared/assets/tokens';
import { TokenWithChainByAsset } from '@/shared/components/logo/TokenWithChain';
import ChevronIcon from '@/shared/icons/flip-ui-kit/large/Chevron';
import SparklesIcon from '@/shared/icons/flip-ui-kit/small/Sparkles';
import { apyToText } from '@/shared/utils/boost';
import { defaultAnimationProps } from '@/shared/utils/consts';
import type TokenAmount from '@/shared/utils/TokenAmount';

type TokensWithBalances = {
  token: ChainflipToken;
  balance: TokenAmount;
  totalApy?: number;
  hasRewards?: boolean;
  disabled?: boolean;
};
const TokenSelectDropdown = ({
  isOpen,
  setIsOpen,
  tokensWithBalances,
  selectedToken,
  setSelectedToken,
  label = 'Asset',
  disabled = false,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  tokensWithBalances: readonly TokensWithBalances[];
  selectedToken: ChainflipToken;
  setSelectedToken: (token: ChainflipToken) => void;
  label?: string;
  disabled?: boolean;
}) => {
  if (!selectedToken) return null;

  const sortedTokensWithBalances = useMemo(
    () =>
      tokensWithBalances.toSorted((a, b) => {
        if (a.disabled !== b.disabled) {
          return a.disabled ? 1 : -1;
        }
        return b.balance.cmp(a.balance);
      }),
    [tokensWithBalances],
  );
  return (
    <DropdownMenu.Root
      open={isOpen}
      onOpenChange={(value) => {
        if (disabled) return;
        setIsOpen(value);
      }}
      modal={false}
    >
      <DropdownMenu.Trigger asChild>
        <div>
          <div
            className={classNames(
              'relative flex h-[55px] grow cursor-default items-center justify-center rounded-md border border-cf-gray-4 bg-cf-gray-3-5 p-4 shadow-grayPop3 transition',
              !disabled &&
                'group cursor-pointer transition hover:border-cf-gray-5 hover:bg-cf-gray-4 ',
            )}
          >
            <div className="absolute left-2 top-[-10px] z-10 select-none px-[6px] text-12 text-cf-light-2">
              <div>{label}</div>
              <div className="absolute inset-x-0 bottom-0 top-[9px] -z-10 bg-cf-gray-3-5 transition group-hover:bg-cf-gray-4" />
            </div>
            <div
              key={selectedToken.symbol}
              className={classNames(
                'flex h-[48px] items-center gap-x-2 transition duration-100 ease-in-out ',
                !disabled && 'hover:cursor-pointer hover:bg-cf-gray-4',
              )}
            >
              <TokenWithChainByAsset asset={selectedToken.chainflipId} size="small" />
              <div className="flex gap-x-1 font-aeonikMedium text-14">
                <div className="text-cf-white">{selectedToken.symbol}</div>
                <div className="text-cf-light-2">on {selectedToken.chain.name}</div>
              </div>
            </div>
            <ChevronIcon
              flip={!isOpen}
              className={classNames('ml-auto text-cf-light-3 transition group-hover:text-cf-white')}
            />
          </div>
        </div>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="z-[100] w-[--radix-dropdown-menu-trigger-width] rounded-md border border-cf-gray-4 bg-cf-gray-3-5 font-aeonikMedium shadow-grayPop2"
          sideOffset={8}
          align="start"
        >
          <MacScrollbar skin="dark" className="max-h-[350px] overflow-y-auto">
            <AnimatePresence mode="wait">
              <motion.div {...defaultAnimationProps} className="flex w-full flex-col text-14">
                {sortedTokensWithBalances.map(
                  ({ token, balance, totalApy, hasRewards, disabled: tokenDisabled }) => (
                    <div
                      key={token.chainflipId}
                      className={classNames(
                        'flex h-[48px] items-center gap-x-2 p-3 transition duration-100 ease-in-out',
                        tokenDisabled && 'opacity-50',
                        !tokenDisabled && 'hover:cursor-pointer hover:bg-cf-gray-4',
                      )}
                      onClick={() => {
                        if (!tokenDisabled) {
                          setSelectedToken(token);
                          setIsOpen(false);
                        }
                      }}
                    >
                      <TokenWithChainByAsset asset={token.chainflipId} size="small" />
                      <div className="flex gap-x-1">
                        <div className="text-cf-white">{token.symbol}</div>
                        <div className="text-cf-light-2">on {token.chain.name}</div>
                      </div>
                      <div className="ml-auto flex flex-col items-end text-13">
                        <div className=" text-white">
                          {balance.toCapped()} {token.symbol}
                        </div>
                        {isNotNullish(totalApy) && (
                          <div className="flex items-center gap-x-1 text-13">
                            {hasRewards && <SparklesIcon className="text-cf-green-3" />}
                            <span className="text-cf-green-3">{apyToText(totalApy)} APY</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ),
                )}
              </motion.div>
            </AnimatePresence>
          </MacScrollbar>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default TokenSelectDropdown;
